import { z } from "zod";
import { ALLIANCE_APPLICATION_MESSAGE_MAX_LENGTH, ALLIANCE_DESCRIPTION_MAX_LENGTH, ALLIANCE_NAME_MAX_LENGTH, ALLIANCE_NAME_MIN_LENGTH, ALLIANCE_NAME_PATTERN, ALLIANCE_TAG_MAX_LENGTH, ALLIANCE_TAG_MIN_LENGTH, ALLIANCE_TAG_PATTERN, } from "../constants/alliances.js";
/**
 * Request schemas for the alliance endpoints. These validate SHAPE only —
 * every gameplay rule (Team-building prerequisite, name/tag uniqueness,
 * membership, role permissions, member cap) is enforced server-side in
 * `apps/functions/src/alliances.ts` against the authoritative Firestore
 * state. See `docs/game-details/alliance_system.md`.
 */
/**
 * Alliance document ids are interpolated into
 * `universes/{u}/alliances/{allianceId}/...` paths, so they get the same
 * shape guard `requireUniverseId` applies to universe ids — notably
 * rejecting `/`, which would otherwise silently change the segment count of
 * the path and surface as a generic 500 instead of a 400.
 */
export const AllianceIdSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z0-9_-]+$/, "allianceId has an invalid format");
/**
 * Same guard, for the user ids that name a member or an applicant. These are
 * interpolated into `.../members/{userId}` and `.../applications/{userId}`
 * exactly as `allianceId` is, so they need the same protection: a `/` here
 * changes the segment count and makes `db.doc()` throw, surfacing as a
 * generic 500 instead of the 400 it is.
 */
export const AllianceUserIdSchema = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9_-]+$/, "userId has an invalid format");
export const AllianceRoleSchema = z.enum(["leader", "officer", "member"]);
export const AllianceNameSchema = z
    .string()
    .trim()
    .min(ALLIANCE_NAME_MIN_LENGTH, `Name must be at least ${ALLIANCE_NAME_MIN_LENGTH} characters`)
    .max(ALLIANCE_NAME_MAX_LENGTH, `Name must be at most ${ALLIANCE_NAME_MAX_LENGTH} characters`)
    .regex(ALLIANCE_NAME_PATTERN, "Name may only contain letters, numbers, spaces and ' . _ -");
export const AllianceTagSchema = z
    .string()
    .trim()
    .min(ALLIANCE_TAG_MIN_LENGTH, `Tag must be at least ${ALLIANCE_TAG_MIN_LENGTH} characters`)
    .max(ALLIANCE_TAG_MAX_LENGTH, `Tag must be at most ${ALLIANCE_TAG_MAX_LENGTH} characters`)
    .regex(ALLIANCE_TAG_PATTERN, "Tag may only contain letters and numbers");
export const AllianceDescriptionSchema = z
    .string()
    .trim()
    .max(ALLIANCE_DESCRIPTION_MAX_LENGTH, `Description must be at most ${ALLIANCE_DESCRIPTION_MAX_LENGTH} characters`);
export const CreateAllianceSchema = z.object({
    name: AllianceNameSchema,
    tag: AllianceTagSchema,
    description: AllianceDescriptionSchema.default(""),
});
/**
 * Both fields optional so the profile form can PATCH either one, but at
 * least one must be present — an empty body would otherwise bump
 * `updatedAt` for no reason.
 */
export const UpdateAllianceSchema = z
    .object({
    description: AllianceDescriptionSchema.optional(),
    recruiting: z.boolean().optional(),
})
    .refine((v) => v.description !== undefined || v.recruiting !== undefined, {
    message: "Nothing to update",
});
export const ApplyToAllianceSchema = z.object({
    allianceId: AllianceIdSchema,
    message: z
        .string()
        .trim()
        .max(ALLIANCE_APPLICATION_MESSAGE_MAX_LENGTH, `Message must be at most ${ALLIANCE_APPLICATION_MESSAGE_MAX_LENGTH} characters`)
        .default(""),
});
export const CancelAllianceApplicationSchema = z.object({
    allianceId: AllianceIdSchema,
});
export const RespondToAllianceApplicationSchema = z.object({
    applicantId: AllianceUserIdSchema,
    accept: z.boolean(),
});
export const AllianceMemberActionSchema = z.object({
    memberId: AllianceUserIdSchema,
});
/**
 * Promote/demote. `leader` is deliberately not assignable here — handing
 * over leadership is its own endpoint (`/transferAllianceLeadership`)
 * because it must also demote the outgoing leader in the same transaction,
 * and an alliance must always have exactly one.
 */
export const SetAllianceMemberRoleSchema = z.object({
    memberId: AllianceUserIdSchema,
    role: z.enum(["officer", "member"]),
});
/** `allianceId` omitted means "my own alliance". */
export const GetAllianceSchema = z.object({
    allianceId: AllianceIdSchema.optional(),
});
export const ListAlliancesSchema = z.object({
    /** Case-insensitive substring match against name and tag. Empty = list all. */
    search: z.string().trim().max(ALLIANCE_NAME_MAX_LENGTH).default(""),
});
//# sourceMappingURL=alliance.schemas.js.map